import React, { useEffect, useState, useRef, useImperativeHandle, useMemo, forwardRef, useCallback } from 'react';

import stringify from '../utils/stableStringify';
import { unpack, pack } from 'msgpackr';
import Toolbar, { Item } from 'devextreme-react/toolbar';
import { Template } from 'devextreme-react/core/template';
import AntLocalization from '../utils/AntLocalization';

import AntArray from '../utils/AntArray';

import { AntGlobal, AntSession } from '../utils/AntContext';
import AntAPI from '../utils/AntAPI';

export default forwardRef(function (props, ref) {
    const appGlobal = AntGlobal();
    const API = useMemo(() => (props.API ? props.API : props.module && props.table ? new AntAPI(props.module, props.table) : undefined), []);

    const forceRender = useCallback(() => {
        setShouldRender((v) => v + 1);
    }, []);

    const [shouldRender, setShouldRender] = useState(1);

    const isRender = useMemo(() => shouldRender + (props.shouldRender ? props.shouldRender : 1), [props.shouldRender, shouldRender]);

    const [data, setData] = useState([]);

    const refCats = useRef();
    const hasReportedLoaded = useRef(false);

    const onSelected = useCallback(
        (addedItems, removedItems, allItems) => {
            if (!addedItems && !removedItems) return;

            let ar = props.selectedItems instanceof Array ? JSON.parse(stringify(props.selectedItems)) : [];
            if (removedItems instanceof Array && removedItems.length > 0) {
                for (let i = 0; i < removedItems.length; i++) {
                    const removedItem = removedItems[i];
                    ar = ar.filter((s) => !(s.name === removedItem.name && s.value === removedItem.value));
                }
            }

            if (addedItems instanceof Array && addedItems.length > 0) {
                for (let i = 0; i < addedItems.length; i++) {
                    const addedItem = addedItems[i];

                    if (props.searchCategories.lookups.every((s) => s.name !== addedItem.name)) continue;

                    let existed = props.selectedItems.find((s) => s.name === addedItem.name && s.value === addedItem.value);
                    if (existed) {
                        ar = ar.filter((s) => !(s.name === addedItem.name && s.value === addedItem.value));
                    } else {
                        if (!props.multiSelect) ar = ar.filter((s) => s.name !== addedItem.name);
                        ar.push({
                            field: addedItem.field,
                            name: addedItem.name,
                            value: addedItem.value,
                            rawValue: addedItem.rawValue
                        });
                    }
                }
            }

            ar = JSON.parse(stringify(ar));

            if (props.onSelectionChanged) props.onSelectionChanged({ selectedItems: ar, allItems: allItems });
        },
        [props.selectedItems, props.multiSelect, props.searchCategories, props.onSelectionChanged]
    );

    const loadData = useCallback(
        async (oldData) => {
            if (props.allowQuery && !props.allowQuery()) return;

            let objSearch = {
                ...props.searchConditions,
                searchText: props.searchText,
                ...props.searchCategories.searchConditions
            };

            if (props.searchCategories && props.searchCategories.lookups instanceof Array)
                props.searchCategories.lookups.forEach((k) => delete objSearch[k.field]);

            delete objSearch.checkActions;

            if (objSearch.searchText === '' || objSearch.searchText === 'undefined') delete objSearch.searchText;

            const staticValues = props.searchCategories.values instanceof Array ? props.searchCategories.values : null;
            let result = null;
            if (!staticValues) {
                result = await API.postSearchPrepare(objSearch, {
                    selectLookups: props.searchCategories.lookups.map((c) => {
                        if (typeof c === 'object' && c.name) return c.name;
                        return c;
                    }),
                    caching: props.searchCategories.caching,
                    freshness: props.searchCategories.freshness
                });
            }
            if (!staticValues && !result) return;

            let ar =
                staticValues
                    ? staticValues.map((value) => {
                          const lookup = props.searchCategories.lookups[0] || {};
                          return {
                              name: lookup.name,
                              rawValue: value?.name || value?.text || value,
                              value: value?.code !== undefined ? value.code : value?.value !== undefined ? value.value : value,
                              field: lookup.field || lookup.name
                          };
                      })
                    : result && result.lookups
                    ? result.lookups
                          .map((l) => {
                              return l.values.map((v) => {
                                  let found = props.searchCategories.lookups.find(
                                      (c) => c === l.name || (typeof c === 'object' && c && c.name === l.name)
                                  );

                                  return {
                                      name: l.name,
                                      rawValue: v,
                                      value:
                                          typeof v === 'object' && v
                                              ? v['code'] !== undefined
                                                  ? v['code']
                                                  : v['value'] !== undefined
                                                  ? v['value']
                                                  : v
                                              : v,
                                      field: found && typeof found === 'object' && found.field ? found.field : l.name
                                  };
                              });
                          })
                          .flat()
                    : [];

            if (props.selectedItems instanceof Array && props.selectedItems.length > 0) {
                for (let i = 0; i < props.selectedItems.length; i++) {
                    const sItem = props.selectedItems[i];
                    if (sItem.value) {
                        if (!ar.some((a) => a.name === sItem.name && a.value === sItem.value)) {
                            ar.push(sItem);
                        }
                    }
                }
            }

            if (props.searchCategories.excludes instanceof Array) ar = ar.filter((a) => props.searchCategories.excludes.includes(a.value) === false);

            ar = ar.map((a) => {
                return {
                    name: a.name,
                    rawValue: a.rawValue,
                    value: a.value,
                    field: a.field
                };
            });

            //             let str1 = stringify(ar.map((a) => a.value + a.name));
            //             let str2 = stringify(oldData.map((a) => a.value + a.name));
            // ;

            let isChanged = stringify(ar.map((a) => a.value + a.name)) !== stringify(oldData.map((a) => a.value + a.name));

            if (isChanged) {
                // Object.assign(oldData.current, JSON.parse(stringify(ar)));
                setData(JSON.parse(stringify(ar)));
                if (props.autoSelectFirst && ar.length > 0) {
                    setTimeout(
                        (o) => {
                            onSelected(o);
                        },
                        500,
                        ar[0]
                    );
                }
            }

            // Consumers use this callback to leave their loading state. It must
            // run for an empty first response too, but repeating it for unchanged
            // data needlessly re-renders the containing POS screen.
            if (props.onLoadedData && (!hasReportedLoaded.current || isChanged)) {
                hasReportedLoaded.current = true;
                props.onLoadedData({ allItems: ar });
            }
        },
        [
            isRender,
            props.allowQuery,
            props.searchConditions,
            props.searchText,
            props.searchCategories,
            props.selectedItems,
            props.onLoadedData,
            props.autoSelectFirst,
            API,
            onSelected
        ]
    );

    const initMainItems = useCallback(
        (oldData) => {
            var items = [];

            let selectedKeys =
                props.selectedItems instanceof Array
                    ? props.selectedItems
                          .map((g) =>
                              typeof g.value === 'object' && g.value
                                  ? g.value['code'] !== undefined
                                      ? g.value['code']
                                      : g.value['value'] !== undefined
                                      ? g.value['value']
                                      : g.value
                                  : g.value
                          )
                          .flat()
                    : [];

            if (oldData instanceof Array) {
                for (let i = 0; i < oldData.length; i++) {
                    let item = oldData[i];

                    let iText =
                        typeof item.rawValue === 'object' && item.rawValue
                            ? item.rawValue['value'] !== undefined
                                ? item.rawValue['value']
                                : item.rawValue['text'] !== undefined
                                ? item.rawValue['text']
                                : item.rawValue['name'] !== undefined
                                ? item.rawValue['name']
                                : item.rawValue['code']
                            : item.rawValue;

                    item.text = AntLocalization.translateText(iText);
                }
                let ar = oldData.filter((btn) => btn.text !== null && btn.text !== undefined && String(btn.text).trim() !== '');

                if (ar.length > 0)
                    items.push({
                        location: 'before',
                        locateInMenu: 'never',
                        widget: 'dxButtonGroup',
                        options: {
                            items: ar,
                            selectedItemKeys: selectedKeys instanceof Array ? selectedKeys : [],
                            keyExpr: props.keyExpr ? props.keyExpr : 'value',
                            // selectionMode: props.searchCategories && Boolean(props.searchCategories.multiSelect) ? 'multiple' : 'single',
                            selectionMode: 'multiple',
                            focusStateEnabled: false,
                            icon: props.icon,
                            text: props.text,
                            width: props.width,
                            buttonTemplate: 'renderButtonOnGroup',
                            stylingMode: props.stylingMode ? props.stylingMode : 'text',
                            onSelectionChanged: (e) => {
                                onSelected(e.addedItems, e.removedItems, oldData);
                            }
                        }
                    });
            }

            return items;
        },
        [props.selectedItems, props.searchCategories, props.keyExpr, props.icon, props.text, props.width, props.stylingMode, onSelected]
    );

    const renderButtonOnGroup = useCallback((btn) => {
        return (
            <div
                style={{
                    verticalAlign: 'center',
                    display: 'inline-flex'
                }}
            >
                <span
                    className="dx-button-text ant-pos-grid-categories-item"
                    style={{
                        // fontWeight: 'bold',
                        color: 'var(--AnteTitleColor)',
                        fontSize: '14px',
                        textTransform: 'uppercase',
                        padding: '2px'
                    }}
                >
                    {btn.text}
                </span>
            </div>
        );
    }, []);

    const [mainItems, setMainItems] = useState(initMainItems(data));

    // useEffect(() => {
    //     loadData([]);
    // }, []);

    useEffect(() => {
        loadData(data);
    }, [loadData]);

    useEffect(() => {
        setMainItems(initMainItems(data));
    }, [data, isRender, initMainItems]);

    useEffect(() => {
        // loadData([]);

        return () => {
            delete refCats.current;
            refCats.current = null;
        };
    }, []);

    return (
        <React.Fragment>
            {/* {mainItems?.length > 0 && ( */}
            <Toolbar
                className={'ant-grid-categories ant-grid-categories-none-border'}
                ref={refCats}
                items={mainItems}
                focusStateEnabled={props.focusStateEnabled}
                disabled={props.disabled}
                elementAttr={{
                    // style: props.selectedItems.length > 0 || (data.length > 1 && props.visible !== false) ? 'display:block' : 'display:none'
                    // style: props.selectedItems.length > 0 || props.visible !== false ? 'display:block' : 'display:none'
                    style: props.visible !== false ? 'display:block' : 'display:none'
                    // style:  'display:block'
                    // style: {
                    //     paddingBottom: '0px !important',
                    //     paddingTop: '0px !important',
                    //     display: mainItems.length > 1 ? 'block !important' : 'none !important'
                    // }
                }}
                multiline={true}
            >
                <Template name={'renderButtonOnGroup'} render={renderButtonOnGroup} />
            </Toolbar>
            {/* )} */}
        </React.Fragment>
    );
});
